/**
 * ./exercise7/Exercise7Workout.js
 *
 * This module **trains speed reading** using **flashing text** from uploaded or pre-selected articles.
 * Users select **reading speed levels**, then attempt to **absorb phrases quickly** as they flash.
 *
 * Key Features:
 * -------------------------------------
 * - Choose a Reading Level (2-10 words per phrase).
 * - Select or Upload an Article.
 * - 10-Minute Timed Flash Reading Session.
 * - Flashing Text Display:
 *   - Shows phrases of selected length.
 *   - Advances every 0.3 seconds.
 *   - Stops after 10 minutes or article completion.
 * - Auto-Redirect to a Comprehension Test.
 *
 */
import React, { useState, useEffect } from "react";
import "./Exercise7Workout.css"; // Import the CSS file
import { useNavigate } from "react-router-dom";
import CloseIcon from "../components/icons/close.svg";
import InfoIcon from "../components/icons/InfoIcon.svg";
import LibraryButton from "./icons/LibraryButton.svg";
import UploadButton from "./icons/UploadButton.svg";
import PlayButton from "../components/icons/play.svg";
import UpButton from "../components/icons/UpButton.svg";
import DownButton from "../components/icons/DownButton.svg";
import { updateUserProgress } from "../utility";
import { DEBUG_TIME, API_BASE_URL } from "../config";

const Exercise7Workout = () => {
  const navigate = useNavigate();
  const TIMER_DURATION = DEBUG_TIME || 60; // seconds
  const [exerciseStep, setExerciseStep] = useState("Select Level");
  const [text, setText] = useState(["No Text"]);
  const [progress, setProgress] = useState(0);
  // phrasesIndex is used to iterate through the phrases array
  const [phrasesIndex, setPhrasesIndex] = useState(0);
  const [isFlashing, setIsFlashing] = useState(false);
  const [articleIndex, setArticleIndex] = useState(0);
  const [endArticleIndex, setEndArticleIndex] = useState(4);
  const [levelSelection, setLevelSelection] = useState(2); // words per phrase
  const [flashCount, setFlashCount] = useState(0);
  const levels = [2, 4, 6, 8, 10];
  const [articleName, setArticleName] = useState("");
  const [timeElapsed, setTimeElapsed] = useState(0);

  const [userData, setUserData] = useState(null);
  const [bookData, setBookData] = useState(null);
  const [articles, setArticles] = useState([]);
  const [uuid, setUuid] = useState("");

  // Splits the full text into an array of phrases.
  // Note: It inserts an empty string after each phrase (optional, for a pause effect).
  const phraseSplitter = (text) => {
    const phrases = [];
    let words = text.join(" ").split(/[ —]+/);
    for (let i = 0; i < words.length; i += levelSelection) {
      phrases.push(words.slice(i, i + levelSelection).join(" "));
      phrases.push(""); // optional pause between phrases
    }
    return phrases;
  };

  let phrases = phraseSplitter(text);

  // Fetch user and book data when the component mounts.
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await response.json();
        setUserData(data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const fetchBookData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/book/books`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await response.json();
        setBookData(data.books);
        setArticles(data.bookOrder);
      } catch (error) {
        console.error("Error fetching book data:", error);
      }
    };

    fetchUserData();
    fetchBookData();
  }, []);

  // Determines if a reading level is unlocked based on user data.
  const isLevelUnlocked = (levelNumber) => {
    if (!userData) return false; // Assume locked if user data isn't loaded
    return userData.exercisesUnlocked[7] >= levelNumber;
  };

  // Timer effect: When flashing is active, update phrasesIndex and timeElapsed every 300ms.
  useEffect(() => {
    if (
      isFlashing &&
      phrasesIndex < phrases.length - 1 &&
      timeElapsed < TIMER_DURATION
    ) {
      const intervalId = setInterval(() => {
        setPhrasesIndex((prevIndex) => prevIndex + 1);
        setTimeElapsed((prevTime) => {
          const newTime = prevTime + 0.3;
          setProgress((newTime / TIMER_DURATION) * 100);
          return newTime;
        });
      }, 300);

      return () => {
        clearInterval(intervalId);
      };
    }

    if (timeElapsed >= TIMER_DURATION) {
      console.log("Timer finished. Saving final progress...");
      // Save progress when the timer finishes.
      updateBookProgressInDB(phrasesIndex);
      setIsFlashing(false);

      const timeoutId = setTimeout(() => {
        navigate("/exercise7test", {
          state: {
            articleText: text,
            articleName: articleName,
            levelSelection: levelSelection,
          },
        });
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [
    isFlashing,
    phrasesIndex,
    phrases.length,
    timeElapsed,
    navigate,
    text,
    articleName,
    levelSelection,
  ]);

  const handleClose = () => {
    navigate("/");
  };

  const goToLibrary = () => {
    setExerciseStep("Choose Article");
  };

  // For legacy article selection.
  const clickBook = (articleText, articleName) => {
    setText(articleText);
    setExerciseStep("Workout");
    setArticleName(articleName);
  };

  // When selecting a book from the library.
  // This also resumes progress by converting stored total words read into the current phrase index.
  const clickBookModern = (bookUuid) => {
    setText(bookData[bookUuid].text);
    setExerciseStep("Workout");
    setArticleName(bookData[bookUuid].title);
    setUuid(bookUuid);
    if (userData && userData.progress && userData.progress[bookUuid] !== undefined) {
      const storedTotalWordsRead = userData.progress[bookUuid]; // stored as total words read
      const newFullPhraseCount = Math.floor(storedTotalWordsRead / levelSelection);
      const newPhraseIndex = newFullPhraseCount * 2; // account for empty strings in the array
      setPhrasesIndex(newPhraseIndex);
    } else {
      setPhrasesIndex(0);
    }
  };

  // Helper: Updates the DB with the total words read.
  // Since your phraseSplitter adds an empty string after each real phrase,
  // we consider a "full phrase" to be 2 increments.
  const updateBookProgressInDB = async (currentPhrasesIndex) => {
    if (!uuid) return; // no book selected
    const fullPhrasesRead = Math.floor(currentPhrasesIndex / 2);
    const totalWordsRead = fullPhrasesRead * levelSelection;
    console.log("Updating book progress in DB. uuid:", uuid, "totalWordsRead:", totalWordsRead);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/updateBookProgress`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ uuid: uuid, newValue: totalWordsRead }),
      });
      console.log("API response status:", response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API update failed:", errorText);
        return;
      }
      console.log("API update succeeded. Updating local userData state...");
      setUserData((ud) => {
        if (!ud) {
          console.warn("No userData found in state, cannot update progress.");
          return ud;
        }
        const newProgress = { ...ud.progress, [uuid]: totalWordsRead };
        console.log("Old progress:", ud.progress);
        console.log("New progress:", newProgress);
        return { ...ud, progress: newProgress };
      });
    } catch (error) {
      console.error("Error updating book progress:", error);
    }
  };

  // The playWordFlasher toggles flashing.
  // When turning flashing off (i.e. when isFlashing is true), it updates progress.
  const playWordFlasher = () => {
    console.log("playWordFlasher triggered. isFlashing:", isFlashing, "uuid:", uuid, "phrasesIndex:", phrasesIndex);
  
    if (isFlashing) {
      // User is stopping reading, so update progress.
      updateBookProgressInDB(phrasesIndex);
    } else {
      console.log("Starting reading. No immediate progress update needed.");
    }
  
    setIsFlashing(!isFlashing);
    console.log("New isFlashing state:", !isFlashing);
  };

  const IncrementArticleDown = () => {
    let prevArticleIndex = articleIndex;
    if (articleIndex - 4 >= 0) {
      setArticleIndex(articleIndex - 4);
      setEndArticleIndex(endArticleIndex - 4);
    } else {
      setArticleIndex(0);
      setEndArticleIndex(endArticleIndex - prevArticleIndex);
    }
  };

  const IncrementArticleUp = () => {
    if (articleIndex + 4 < articles.length) {
      setArticleIndex(articleIndex + 4);
      setEndArticleIndex(endArticleIndex + 4);
    } else {
      setArticleIndex(articles.length - 4);
      setEndArticleIndex(articles.length - 1);
    }
  };

  const renderExercise = () => {
    if (exerciseStep === "Select Level") {
      return (
        <>
          <h3>Pick Your Level</h3>
          <div>
            {levels.map((level, index) => (
              <button
                key={level}
                className={`duration-button ${levelSelection === level ? "selected" : ""}`}
                onClick={() => setLevelSelection(level)}
                disabled={!isLevelUnlocked(index + 1)}
                style={{
                  backgroundColor: isLevelUnlocked(index + 1) ? "#4CAF50" : "#CCCCCC",
                  cursor: isLevelUnlocked(index + 1) ? "pointer" : "not-allowed",
                }}
              >
                {level}
              </button>
            ))}
          </div>
          <button className="next-button" onClick={() => setExerciseStep("Upload File")}>
            Next
          </button>
        </>
      );
    }
    if (exerciseStep === "Upload File") {
      return (
        <div className="file-upload-container">
          <h3>Upload or Choose from Library</h3>
          <div>
            <img src={UploadButton} alt="upload button" />{" "}
            {/* NEXT STEP: file upload function */}
            <img
              src={LibraryButton}
              alt="library button"
              onClick={goToLibrary}
              className="library-button"
            />
          </div>
        </div>
      );
    }
    if (exerciseStep === "Choose Article") {
      return (
        <div>
          <h3>Choose an Unread Article</h3>
          <div className="article-picker">
            <img src={DownButton} className="library-button" onClick={IncrementArticleDown} />
            {articles.slice(articleIndex, endArticleIndex).map((article) =>
              userData.completedBooks.includes(article) ? (
                <>
                  <p className="text-overlay">completed</p>
                  <img
                    src={"data:image/jpeg;base64," + bookData[article].cover}
                    alt={bookData[article].title}
                    className={"bookCover read"}
                  />
                </>
              ) : (
                <img
                  key={article}
                  src={"data:image/jpeg;base64," + bookData[article].cover}
                  alt={bookData[article].title}
                  className={"bookCover"}
                  onClick={() => clickBookModern(article)}
                  width="200px"
                  height="200px"
                />
              )
            )}
            <img src={UpButton} className="library-button" onClick={IncrementArticleUp} />
          </div>
        </div>
      );
    }
    if (exerciseStep === "Workout" && isFlashing) {
      return (
        <div>
          <div className="flashing-text">{phrases[phrasesIndex]}</div>
        </div>
      );
    }
  };

  return (
    <div className="exercise-container">
      <div className="header-bar">
        <div className="close-icon-wrapper">
          <img src={CloseIcon} alt="close icon" className="close-icon" onClick={handleClose} />
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
        <div className="close-icon-wrapper">
          <img src={PlayButton} alt="play button" onClick={playWordFlasher} width="50px" />
        </div>
      </div>
      <div className="main-content">{renderExercise()}</div>
      <div className="instruction-box">
        <img src={InfoIcon} alt="info icon" className="instruction-icon" />
        {exerciseStep !== "Workout" ? (
          <p>Upload or choose an article you have not read before.</p>
        ) : (
          <p>Focus on the flashing words.</p>
        )}
      </div>
    </div>
  );
};

export default Exercise7Workout;